import React from 'react';
import './MarketInfo.css';

export interface MarketInfoProps {
  markets: Array<{
    id?: number;
    name?: string;
    marketType?: string;
    currency?: string;
    isEnabled?: boolean;
    isTpf?: boolean;
    cashPool?: number;
    minReturnAmount?: number;
    minimumIncomeAmount?: number;
    supplierCreditLine?: number;
    maxApr?: number;
    maxDiscount?: number;
    productType?: string;
    timezone?: string;
    payDate?: string;
    nextClearTime?: string;
    created?: string;
    updated?: string;
  }>;
  count: number;
  context?: string;
}

export const MarketInfo: React.FC<MarketInfoProps> = ({ markets, count, context }) => {
  if (markets.length === 0) {
    return (
      <div className="market-info-container">
        <div className="market-info-header">
          <h3>📊 Market Information</h3>
          <span className="market-count">No markets found</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (value?: number, currency?: string) => {
    if (value === undefined || value === null) return 'N/A';
    const currencyCode = currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="market-info-container">
      <div className="market-info-header">
        <h3>📊 Market Information</h3>
        <span className="market-count">
          {count} {count === 1 ? 'market' : 'markets'}
        </span>
      </div>
      {context && <p className="market-info-context">{context}</p>}

      <div className="markets-grid">
        {markets.map((market, index) => (
          <div key={market.id || index} className="market-card">
            <div className="market-card-header">
              <div className="market-icon">📊</div>
              <div className="market-name-section">
                <h4 className="market-name">{market.name || 'Unnamed Market'}</h4>
                {market.marketType && (
                  <div className="market-type">
                    <span className="type-badge">{market.marketType}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="market-status">
              {market.isEnabled !== undefined && (
                <span className={`status-badge ${market.isEnabled ? 'enabled' : 'disabled'}`}>
                  {market.isEnabled ? '✓ Enabled' : '✗ Disabled'}
                </span>
              )}
              {market.isTpf && <span className="status-badge tpf">TPF</span>}
            </div>

            <div className="market-details">
              {market.currency && (
                <div className="market-detail-item">
                  <span className="detail-label">Currency:</span>
                  <span className="detail-value">{market.currency}</span>
                </div>
              )}

              {market.productType && (
                <div className="market-detail-item">
                  <span className="detail-label">Product Type:</span>
                  <span className="detail-value">{market.productType}</span>
                </div>
              )}

              {market.cashPool !== undefined && (
                <div className="market-detail-item">
                  <span className="detail-label">Cash Pool:</span>
                  <span className="detail-value">{formatCurrency(market.cashPool, market.currency)}</span>
                </div>
              )}

              {market.minReturnAmount !== undefined && (
                <div className="market-detail-item">
                  <span className="detail-label">Min Return:</span>
                  <span className="detail-value">{formatCurrency(market.minReturnAmount, market.currency)}</span>
                </div>
              )}

              {market.minimumIncomeAmount !== undefined && (
                <div className="market-detail-item">
                  <span className="detail-label">Min Income:</span>
                  <span className="detail-value">{formatCurrency(market.minimumIncomeAmount, market.currency)}</span>
                </div>
              )}

              {market.supplierCreditLine !== undefined && (
                <div className="market-detail-item">
                  <span className="detail-label">Supplier Credit Line:</span>
                  <span className="detail-value">{formatCurrency(market.supplierCreditLine, market.currency)}</span>
                </div>
              )}

              {market.maxApr !== undefined && (
                <div className="market-detail-item">
                  <span className="detail-label">Max APR:</span>
                  <span className="detail-value">{(market.maxApr * 100).toFixed(2)}%</span>
                </div>
              )}

              {market.maxDiscount !== undefined && (
                <div className="market-detail-item">
                  <span className="detail-label">Max Discount:</span>
                  <span className="detail-value">{(market.maxDiscount * 100).toFixed(2)}%</span>
                </div>
              )}

              {market.timezone && (
                <div className="market-detail-item">
                  <span className="detail-label">Timezone:</span>
                  <span className="detail-value">{market.timezone}</span>
                </div>
              )}

              {market.payDate && (
                <div className="market-detail-item">
                  <span className="detail-label">Pay Date:</span>
                  <span className="detail-value">{formatDate(market.payDate)}</span>
                </div>
              )}

              {market.nextClearTime && (
                <div className="market-detail-item">
                  <span className="detail-label">Next Clear:</span>
                  <span className="detail-value">{formatDate(market.nextClearTime)}</span>
                </div>
              )}
            </div>

            {(market.created || market.updated) && (
              <div className="market-timestamps">
                {market.created && (
                  <div className="timestamp">
                    <span className="timestamp-label">Created:</span>
                    <span className="timestamp-value">{formatDate(market.created)}</span>
                  </div>
                )}
                {market.updated && (
                  <div className="timestamp">
                    <span className="timestamp-label">Updated:</span>
                    <span className="timestamp-value">{formatDate(market.updated)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
